import React, { createContext, useState, useContext, FC, ReactNode, useEffect } from 'react';
import api from './api';

interface User {
  id: string;
  email: string;
  role: string;
  organizationId: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const userData = await api('/auth/me');
      setUser(userData);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await api('/auth/sign-in', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(response.user);
  };

  const logout = async () => {
    try {
      await api('/auth/sign-out', {
        method: 'POST',
      });
    } catch (error) {
      // Even if the logout request fails, clear local state
      console.error('Logout request failed:', error);
    } finally {
      setUser(null);
    }
  };

  // Check authentication status on app load
  useEffect(() => {
    checkAuth();
  }, []);

  const isAuthenticated = user !== null;

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading, 
      login, 
      logout, 
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 